import { useState, useEffect, useRef } from 'react'
import { Outlet, Link, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'
import { ComingSoonBadge } from './ComingSoonBadge'

interface PersonMe {
  id: string
}

interface NavItem {
  path: string
  label: string
  comingSoon?: boolean
}

const labUrl = window.location.hostname.startsWith('test.')
  ? 'https://test.rtgs-lab.com'
  : 'https://rtgs-lab.com'

export function Layout() {
  const { user, firebaseUser, logout } = useAuth()
  const location = useLocation()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  const isAdmin = user?.role === 'admin' || user?.role === 'partner'

  // Close menu on outside click
  useEffect(() => {
    if (!showUserMenu) return
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
        setShowUserMenu(false)
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [showUserMenu])

  // Check if user has a linked Person record and if they have reports
  const { data: myPerson } = useQuery({
    queryKey: ['people', 'me'],
    queryFn: () => api.get<PersonMe>('/people/me').catch(() => null),
    retry: false,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  })

  const hasPersonRecord = !!myPerson

  const navItems: NavItem[] = [
    { path: '/', label: 'Dashboard' },
    { path: '/projects', label: 'Projects' },
    ...(hasPersonRecord ? [{ path: '/my-work', label: 'My Work' }] : []),
    { path: '/files', label: 'Files' },
    { path: '/apps', label: 'Apps' },
    { path: '/services', label: 'Services' },
    ...(isAdmin ? [{ path: '/admin', label: 'Admin' }] : []),
  ]

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const initial = (firebaseUser?.email?.[0] || '?').toUpperCase()

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--color-bg)]/90 backdrop-blur-sm border-b border-[var(--color-border)]">
        {/* Row 1: Brand + User */}
        <div className="px-4 sm:px-8 md:px-16 lg:px-24 pt-4 pb-2 flex justify-between items-center">
          {/* Breadcrumb — no wrap */}
          <div className="flex items-center gap-2 font-sans font-medium text-lg whitespace-nowrap">
            <a
              href={labUrl}
              className="hidden md:inline no-underline text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
            >
              RTGS Lab
            </a>
            <span className="hidden md:inline text-[var(--color-text-tertiary)]">/</span>
            <Link
              to="/"
              className="no-underline text-[var(--color-text)]"
            >
              Portal
            </Link>
          </div>

          {/* Desktop user info (sm+) */}
          <div className="hidden sm:flex items-center gap-4 font-sans">
            <span className="text-sm text-[var(--color-text-secondary)]">
              {firebaseUser?.email}
            </span>
            {user?.role && (
              <span className="text-xs px-2 py-1 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded">
                {user.role}
              </span>
            )}
            <button
              onClick={() => logout()}
              className="text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
            >
              Logout
            </button>
          </div>

          {/* Mobile user icon (below sm) */}
          <div className="relative sm:hidden" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-8 h-8 rounded-full bg-[var(--color-bg-secondary)] border border-[var(--color-border)] font-sans text-sm text-[var(--color-text)] flex items-center justify-center"
            >
              {initial}
            </button>
            {showUserMenu && (
              <div className="absolute right-0 top-10 w-56 bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-3 shadow-lg">
                <div className="text-sm text-[var(--color-text-secondary)] mb-2 truncate">
                  {firebaseUser?.email}
                </div>
                {user?.role && (
                  <div className="mb-2">
                    <span className="text-xs px-2 py-1 bg-[var(--color-bg)] border border-[var(--color-border)] rounded">
                      {user.role}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => logout()}
                  className="w-full text-left text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] transition-colors"
                >
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Row 2: Nav links */}
        <div className="px-4 sm:px-8 md:px-16 lg:px-24 pb-3 flex gap-6 sm:gap-8 overflow-x-auto scrollbar-hide">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`no-underline font-sans text-sm whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                isActive(item.path)
                  ? 'text-[var(--color-accent)]'
                  : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text)]'
              }`}
            >
              {item.label}
              {item.comingSoon && <ComingSoonBadge />}
            </Link>
          ))}
        </div>
      </nav>

      {/* Main content */}
      <main className="pt-28 sm:pt-24 px-4 sm:px-8 md:px-16 lg:px-24 py-8">
        <Outlet />
      </main>
    </div>
  )
}
